import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  legalEntityKeys,
  legalEntityRepository,
} from '@webapp/platform/legal-entities/data-access/repositories/LegalEntityRepository'

interface ReactivateLegalEntityParams {
  uuid: string
}

/**
 * Reactivates an inactive LegalEntity and invalidates the cached LegalEntity
 * list. That single query feeds both the Active/Inactive list tabs and the
 * details view (`useQueryLegalEntity` derives a single entity from the list),
 * so invalidating it refreshes both surfaces — the row moves back to the
 * Active tab and the detail badge flips to Active.
 */
export const useMutateReactivateLegalEntity = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<void, RepoError, ReactivateLegalEntityParams>({
    mutationFn: ({ uuid }: ReactivateLegalEntityParams) => {
      return legalEntityRepository.reactivate(uuid)
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.list })
    },
  })

  return {
    reactivate: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  }
}
